import { useState } from "react";
import { MapPin, Mail, Phone, Clock, MessageCircle, Send } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SITE, whatsappHref, callHref } from "@/data/site";

const empty = { full_name: "", phone: "", email: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    const subject = `Website Enquiry — ${form.full_name}`;
    const body = `Name: ${form.full_name}\nPhone: ${form.phone}\nEmail: ${form.email}\n\nMessage:\n${form.message}`;
    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success("Opening your email app to send the message.");
    setForm(empty);
  };

  const contactCards = [
    { icon: MapPin, title: "Visit Us", lines: [SITE.address.line1, `${SITE.address.line2}, ${SITE.address.city}`] },
    { icon: Phone, title: "Call Us", lines: SITE.phones.map((p) => `${p.number} (${p.label})`) },
    { icon: Mail, title: "Email Us", lines: [SITE.email] },
    { icon: Clock, title: "Working Hours", lines: SITE.hours.map((h) => `${h.day}: ${h.time}`) },
  ];

  return (
    <main data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Get in Touch with UNIRAD"
        subtitle="Have a question or want to book a test? Reach out — we're here to help you with accurate, caring diagnostics."
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
          {/* Left: info + map */}
          <Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              {contactCards.map((c) => (
                <div key={c.title} data-testid={`contact-card-${c.title.toLowerCase().replace(/\s/g, "-")}`} className="rounded-2xl border border-border bg-brand-mist p-5">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand text-white">
                    <c.icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <h3 className="mt-4 font-heading font-semibold text-brand-navy">{c.title}</h3>
                  <div className="mt-1 space-y-0.5">
                    {c.lines.map((l) => <p key={l} className="text-sm text-muted-foreground leading-snug">{l}</p>)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex gap-3">
              <a href={whatsappHref()} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid="contact-whatsapp-btn">
                <Button className="w-full h-12 rounded-full bg-[#25D366] hover:bg-[#1faa55] gap-2">
                  <MessageCircle className="h-5 w-5" /> WhatsApp
                </Button>
              </a>
              <a href={callHref} className="flex-1" data-testid="contact-call-btn">
                <Button variant="outline" className="w-full h-12 rounded-full border-brand/30 text-brand gap-2 hover:bg-brand-soft">
                  <Phone className="h-5 w-5" /> Call Now
                </Button>
              </a>
            </div>

            <div className="mt-5 rounded-2xl overflow-hidden border border-border h-72">
              <iframe
                title="UNIRAD Diagnostics map"
                data-testid="contact-map"
                src={SITE.mapEmbed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-border bg-card shadow-xl p-7 sm:p-9">
              <h2 className="font-heading text-2xl font-semibold text-brand-navy">Send us a message</h2>
              <p className="text-sm text-muted-foreground mt-1">Submitting will open your email app to send a message to {SITE.email}.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Full Name *</Label>
                  <Input id="c-name" data-testid="contact-name" className="h-12" placeholder="Your name" value={form.full_name} onChange={set("full_name")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-phone">Phone *</Label>
                    <Input id="c-phone" data-testid="contact-phone" className="h-12" placeholder="Mobile number" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-email">Email</Label>
                    <Input id="c-email" type="email" data-testid="contact-email" className="h-12" placeholder="you@email.com" value={form.email} onChange={set("email")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-msg">Message</Label>
                  <Textarea id="c-msg" data-testid="contact-message" rows={5} placeholder="How can we help you?" value={form.message} onChange={set("message")} />
                </div>
                <Button type="submit" data-testid="contact-submit" className="w-full h-12 rounded-full bg-brand hover:bg-brand-light text-base gap-2 shadow-md shadow-brand/25">
                  <Send className="h-4 w-4" /> Send via Email
                </Button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
